import { Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter } from 'rxjs/operators';
import { TenantInnerService } from 'src/app/_services/tenant-inner.service';
import { environment } from '../../../environments/environment';
import {
  ADMIN_ROLE,
  Feature,
  MERCK_TENANT,
  SIDEBAR_AUTO_COLLAPSE_MAX_WIDTH,
  SIDEBAR_COLLAPSE_ROUTES,
} from '../../_helpers/Constants';
import { UserType } from '../../_admin/user-managment/models/UserManagementDTO';
import { Tenant } from '../../_models/tenant';
import { NotificationService } from '../../_services/notification.service';
import { TokenService } from '../../_services/token.service';
import { SessionGuardService } from '../../_services/session-guard.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SidebarComponent implements OnInit, OnChanges {
  @Input() isExpanded: boolean = false;
  @Input() isTooltipEnabled: boolean = false;
  @Output() toggleSidebar: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() tooltipEnabledChange = new EventEmitter<boolean>();
  @ViewChild('abwasserDropdown') abwasserDropdown?: BsDropdownDirective;
  @ViewChild('invoicingDropdown') invoicingDropdown?: BsDropdownDirective;
  @ViewChild('marketingDropdown') marketingDropdown?: BsDropdownDirective;
  @ViewChild('gebaeudebewertungDropdown') gebaeudebewertungDropdown?: BsDropdownDirective;

  @HostListener('window:scroll')
  doSomething() {
    this.top = !window.scrollY;
  }

  handleSidebarToggle = () => {
    this.toggleSidebar.emit(!this.isExpanded);
  };
  readonly Feature = Feature;
  isAdminView = false;
  isUserManagement = false;
  isAdminRole = false;
  isPartnerAdmin = false;
  isTenantAdmin = false;
  currentFeatures: number[] = [];
  tenants: Tenant[] = [];
  curTenant: Tenant | undefined;
  isLoading = true;
  isMobile = false;
  isTablet = false;
  userName = '';
  userEmail = '';
  pop: any;
  adminNotificationCount = 0;
  top = !window.scrollY;
  private collapsedBySubItemClick = false;

  ngOnChanges(changes: SimpleChanges) {
    // A collapsed sidebar cannot show the sub items, so however it gets
    // collapsed (toggle, sub item click, route change), the open group closes with it.
    if (changes['isExpanded'] && !this.isExpanded) {
      this.closeAllDropdowns();
    }
  }

  constructor(
    public router: Router,
    private tokenService: TokenService,
    private deviceService: DeviceDetectorService,
    private notificationService: NotificationService,
    public tenantInnerService: TenantInnerService,
    private _location: Location,
    private sessionGuard: SessionGuardService
  ) {}

  hasFeature(feature: Feature): boolean {
    if (this.isAdminRole) return true;
    return this.currentFeatures.includes(feature);
  }

  /**
   * "Überflutung" map link: only for the Merck tenant, only on production builds, and only
   * for users with the Maps permission (admins pass via hasFeature).
   */
  get isMerckFloodMapAvailable(): boolean {
    return (
      environment.production && this.curTenant?.name === MERCK_TENANT && this.hasFeature(Feature.Maps)
    );
  }

  ngOnInit() {
    const token = this.tokenService.getToken();
    if (token !== null) {
      const decoded: any = jwt_decode(token);
      const roles = decoded[environment.roleKey];
      if (roles) {
        this.isAdminRole = roles.includes(ADMIN_ROLE);
      }
    }
    this.tenantInnerService.curTenant$.subscribe((tenant) => {
      if (tenant) this.curTenant = tenant;
      this.currentFeatures = tenant?.features ?? [];
      this.isPartnerAdmin = tenant?.userType === UserType.PartnerAdmin;
      this.isTenantAdmin = tenant?.userType === UserType.TenantAdmin;
    });
    this.isAdminView = this.router.url.includes('/admin');
    this.isUserManagement = this.router.url.includes('/benutzermanagement');

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const isAbwasserRoute =
        event.urlAfterRedirects.includes('/wastewater/') || event.urlAfterRedirects.includes('/construction');
      const isCollapseRoute = SIDEBAR_COLLAPSE_ROUTES.some((route) => event.urlAfterRedirects.includes(route));
      const isHomeRoute = event.urlAfterRedirects === '/' || event.urlAfterRedirects === '';
      const shouldAutoCollapse = window.innerWidth <= SIDEBAR_AUTO_COLLAPSE_MAX_WIDTH;
      // Picking a sub item collapses the sidebar, so the auto-expand below must
      // not undo that for the navigation the very same click triggered.
      const collapsedBySubItem = this.collapsedBySubItemClick;
      this.collapsedBySubItemClick = false;

      if (isCollapseRoute && this.isExpanded && shouldAutoCollapse) {
        this.toggleSidebar.emit(false);
      } else if (
        !collapsedBySubItem &&
        (isHomeRoute || !isCollapseRoute) &&
        !this.isExpanded &&
        shouldAutoCollapse
      ) {
        this.toggleSidebar.emit(true);
      }

      // Always hide the dropdown if not an Abwasser route and dropdown is open
      if (!isAbwasserRoute && this.abwasserDropdown?.isOpen) {
        this.abwasserDropdown.hide();
      }
    });
    // Tenant restoration itself is owned by TenantInnerService.ensureUserTenant() - it's the one
    // path (also used by the route guards) that both loads the tenant list and restores the
    // active tenant from session/saved state, and it's a no-op once a tenant is already active.
    // Sidebar used to duplicate that restore logic inline here; doing so raced the guards'
    // tenantId-link override (see createFeatureGuard) whenever both ran on the same cold load,
    // since whichever finished last silently won and overwrote the other's tenant.
    this.tenantInnerService.tenants$.subscribe((tenants) => {
      this.tenants = tenants;
    });
    this.tenantInnerService.ensureUserTenant().subscribe(() => {
      this.notificationService.adminNotifications.subscribe((n) => {
        this.adminNotificationCount = this.adminNotificationCount + n;
      });
      this.isLoading = false;
    });
    this.tokenService.userNameSubject.subscribe((data) => {
      this.userName = data;
    });
    this.userEmail = this.tokenService.getUserEmail();
    this.isMobile = this.deviceService.isMobile();
    this.isTablet = this.deviceService.isTablet();
  }

  SetTenant(tenant: Tenant) {
    // Route through TenantInnerService instead of writing tokenService/sessionStorage directly -
    // it's what keeps curTenant$ (and this.curTenant/this.currentFeatures, via the curTenant$
    // subscription above) in sync, and it's now the source ensureUserTenant() trusts for every
    // later guarded navigation. Writing storage here without it left the header frozen on the
    // old tenant and, worse, left that stale tenant active for subsequent route guards.
    this.tenantInnerService.setTenant(tenant, undefined, true);
    this.tenantInnerService.syncTenantIdInUrl();
  }

  goToUserManagementDashboard(event?: MouseEvent) {
    if (event?.ctrlKey || event?.metaKey) {
      return;
    }
    event?.preventDefault();
    this.isAdminView = false;
    this.isUserManagement = !this.isUserManagement;
    this.router.navigateByUrl(this.isUserManagement ? '/benutzermanagement/manage-access' : '/');
  }

  GoToAdminDashboard(event?: MouseEvent) {
    if (event?.ctrlKey || event?.metaKey) {
      return;
    }
    event?.preventDefault();
    this.isUserManagement = false;
    this.isAdminView = !this.isAdminView;
    this.adminNotificationCount = 0;
    this.router.navigateByUrl(this.isAdminView ? '/admin' : '/');
  }

  goHome() {
    this.router.navigateByUrl('');
  }
  closeMenu() {
    if (this.isExpanded && this.deviceService.isMobile()) {
      this.handleSidebarToggle();
    }
  }
  async logout(): Promise<void> {
    if (!(await this.sessionGuard.confirmSessionChange())) return;
    this.tokenService.signOut();
    this.router.navigateByUrl('login').then(() => {
      window.location.reload();
    });
  }

  manageAccount() {
    this.router.navigateByUrl('settings/profile');
  }

  reNavigate(url: string): void {
    if (this.router.url === url) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([url]);
      });
    }
  }

  async switchTenant(tenant: Tenant): Promise<void> {
    // These detail pages are each scoped to one resource (an id in the URL, resolved against a
    // `tenant`/`source` captured on init) and already own their reaction to a manual tenant
    // switch via TokenService.tenantChanged$ + wasManualChange() - they navigate themselves back
    // to their (id-less) list route and reload (see e.g. EngineerToolPageComponent's
    // handleRuntimeTenantSwitch/goBackAndReload). So here we only flip the active-tenant state
    // and let the page handle its own navigation instead of racing it with one of our own.
    // Strip the query string before matching - these detail links are frequently opened with
    // ?tenant=&source=&tenantId= attached, and the trailing `$` must anchor on the path's end,
    // not get pushed past by a query string the GUID is never actually followed by.
    const path = this.router.url.split(/[?#]/)[0];
    const isDetailPage =
      /\/(ticket|upload-detail|special-system|documentation-site-detail|construction\/site|irap\/building-risk\/details|pipe-inspection|manhole-inspection|kanalyze\/details|kanalyze\/pipes|engineer-tool\/details|engineer-tool\/pipes)\/[\w-]+$/.test(
        path
      ) || /guid=/.test(this.router.url);
    if (isDetailPage) {
      this.tenantInnerService.switchTenant(tenant, undefined, true, true);
    } else {
      const isSwitched = await this.tenantInnerService.switchTenant(tenant, undefined, true);
      if (!isSwitched) {
        return;
      }
      // A stale ?tenantId= from a shared/deep link would otherwise survive the reload and get
      // picked back up by createFeatureGuard, silently undoing the tenant the user just picked
      // here. Sync it to the newly chosen tenant before reloading so the guard sees a match.
      const url = new URL(window.location.href);
      if (url.searchParams.has('tenantId')) {
        url.searchParams.set('tenantId', tenant.id);
        window.location.href = url.toString();
      } else {
        window.location.reload();
      }
    }
  }

  onDropdownOpen(isOpen: boolean, dropdownName: string) {
    if (isOpen) {
      if (dropdownName === 'abwasser' && this.gebaeudebewertungDropdown?.isOpen) {
        this.gebaeudebewertungDropdown.hide();
      } else if (dropdownName === 'gebaeudebewertung' && this.abwasserDropdown?.isOpen) {
        this.abwasserDropdown.hide();
      }
    }
  }

  closeExpanded() {
    if (window.innerWidth <= SIDEBAR_AUTO_COLLAPSE_MAX_WIDTH && this.isExpanded) {
      this.collapsedBySubItemClick = true;
      this.toggleSidebar.emit(false);
    }
  }

  /**
   * The sub menus are rendered as sub items inside the sidebar, so a collapsed
   * sidebar has no room to show them: opening a group expands it first. The
   * dropdown directive opens the group off the same click.
   */
  expandForGroup() {
    if (!this.isExpanded) {
      this.toggleSidebar.emit(true);
    }
  }

  closeAllDropdowns() {
    [
      this.gebaeudebewertungDropdown,
      this.abwasserDropdown,
      this.invoicingDropdown,
      this.marketingDropdown,
    ].forEach((dropdown) => {
      if (dropdown?.isOpen) {
        dropdown.hide();
      }
    });
  }

  get isMerckTenant(): boolean {
    return (this.tokenService.GetTenant() || '').toLowerCase() === 'merck';
  }

  get invoicingMoveDownOffset(): string {
    // Both invoice items are always visible (no role conditions)
    return `${2 * 3}rem`;
  }

  get hasAnyAbwasserFeature(): boolean {
    if (this.isAdminRole) return true;
    return [
      Feature.Tickets, Feature.ConstructionSite, Feature.SewerDashboard,
      Feature.Upload, Feature.DataExport, Feature.DataCheck,
      Feature.EngineersDamageClass, Feature.PipeInspection, Feature.PipeInspectionEditor,
      Feature.AdoptionPipeInspection, Feature.SewerTinderV2, Feature.SewerTinderV3,
      Feature.ManholeInspection, Feature.SewerTinder, Feature.SpecialSystem,
    ].some((f) => this.currentFeatures.includes(f));
  }

  closeOtherDropdowns(currentDropdown: 'abwasser' | 'invoicing' | 'marketing') {
    if (currentDropdown !== 'abwasser' && this.abwasserDropdown?.isOpen) {
      this.abwasserDropdown.hide();
    }
    if (currentDropdown !== 'invoicing' && this.invoicingDropdown?.isOpen) {
      this.invoicingDropdown.hide();
    }
    if (currentDropdown !== 'marketing' && this.marketingDropdown?.isOpen) {
      this.marketingDropdown.hide();
    }
  }
}
